import React, { useEffect, useState } from "react"
import { Link } from "gatsby"
import { FaChevronRight } from "react-icons/fa"

import { ChildPagesFormatted } from "contexts/settings/Navigation/navigation-types"
import { useLayout } from "contexts/components/LayoutContext"
import { useSideNav } from "hooks/useSideNav"

import { SubNavList } from "./SubNavList"
import "../side-nav.module.scss"
import "./sub-nav.module.scss"

interface SubNavProps {
  childPages: ChildPagesFormatted[]
  id: string
  linkText: string
  parentSlug: string
}

export const SubNav = ({
  childPages,
  id,
  linkText,
  parentSlug
}: SubNavProps) => {
  let LinkButton

  const {
    breadcrumbLocation,
    linkedPagesUrlPathCodex
  } = useLayout()

  const {
    activeSubNavId,
    handleCloseNav,
    handleToggleSubNav,
    isNavOpen
  } = useSideNav()

  const getAllPagesLocationFromSlug = (childPages: ChildPagesFormatted[]) => {
    const HOMEPAGE_SLUG = '/'
    const allPagesLocationData = [] as Record<string, boolean>[]

    childPages.forEach(({ slug }) => {
      let singlePageLocationCheck = {}

      const pageSlug = slug.startsWith('/')
        ? slug
        : `/${slug}`
      const parentPageSlug = parentSlug.startsWith('/')
        ? parentSlug
        : `/${parentSlug}`

      const isCurrentChildPageLocation = pageSlug === breadcrumbLocation,
            isCurrentParentPageLocation = parentPageSlug === breadcrumbLocation,
            isParentPageNotHomepage = parentPageSlug !== HOMEPAGE_SLUG,
            isWithinParentAndNotHomepage =
              isCurrentParentPageLocation && isParentPageNotHomepage

      singlePageLocationCheck = {
        pageSlug: pageSlug,
        parentPageSlug: parentPageSlug,
        isCurrentChildPageLocation: isCurrentChildPageLocation,
        isCurrentParentPageLocation: isCurrentParentPageLocation,
        isParentPageNotHomepage: isParentPageNotHomepage,
        isWithinParentAndNotHomepage: isWithinParentAndNotHomepage
      }

      allPagesLocationData.push(singlePageLocationCheck)
    })

    return allPagesLocationData
  }

  const getCurrentPageLocation = (childPages: ChildPagesFormatted[]) => {
    let currentPageData
    const pagesLocationData = getAllPagesLocationFromSlug(childPages)

    pagesLocationData.forEach(location => {
      const { isCurrentChildPageLocation } = location

      if (isCurrentChildPageLocation)
        currentPageData = location
    })

    return currentPageData
      ? currentPageData
      : undefined
  }

  const getCurrentPageSlug = () => {
    const currentPage = getCurrentPageLocation(childPages)
    return currentPage ? currentPage['pageSlug'] : ''
  }

  const handleSubNav = (activeItemId: string) => handleToggleSubNav(activeItemId)

  const currentPageSlug = getCurrentPageSlug()

  const [isCurrentPage, ] = useState(currentPageSlug)
  const [isLocation, setLocation] = useState(false)
  const [isOpen, toggleOpen] = useState(activeSubNavId === id)

  const checkChildElements = () => {
    const currentPage = getCurrentPageLocation(childPages)

    if (currentPage) {
      const {
        isCurrentChildPageLocation,
        isWithinParentAndNotHomepage
      } = currentPage

      if (isCurrentChildPageLocation || isWithinParentAndNotHomepage) {
        handleToggleSubNav(id)
        setLocation(true)
        return
      }
    }
  }

  useEffect(() => {
    toggleOpen(activeSubNavId === id)
  }, [activeSubNavId])

  useEffect(() => {
    let mounted = true

    if (mounted && !isLocation)
      checkChildElements()

    return () => {
      mounted = false
    }
  }, [isLocation])

  const isCurrentPageClassName = isCurrentPage === breadcrumbLocation
    ? ' side-nav__link--current'
    : ' '

  const isCurrentPageAndNavlinkClosed = !isOpen && isLocation
    ? ' side-nav__link--current-closed'
    : ' '

  const isNavLinkActiveClassName = isOpen || isLocation
    ? ' side-nav__link--active'
    : ' '

  const subNavClass = ` sub-nav__button${isLocation
    ? '  sub-nav__button--active'
    : ' '}`

  let linkClassList = [
    'side-nav__link',
    'u-flex-center-align',
    isNavLinkActiveClassName,
    isCurrentPageClassName,
    isCurrentPageAndNavlinkClosed,
    subNavClass
  ]

  const linkClass = linkClassList.join(' '),
        linkId = `${linkText.toLowerCase().replaceAll(' ', '_')}`,
        linkAria = `id-${linkId}-subtree`

  if (parentSlug) {
    const pageSlug = linkedPagesUrlPathCodex[parentSlug] || '404',
          linkTo = pageSlug?.startsWith('/')
            ? pageSlug
            : `/${pageSlug}`

    LinkButton = (
      <span
        aria-expanded={ isOpen }
        aria-owns= { linkAria }
        className={ linkClass }
        onClick={ () => handleSubNav(id) }
        role="treeitem"
      >
        <Link
          onClick={ handleCloseNav }
          to={ linkTo }
        >
          { linkText }
        </Link>
        <button
          aria-label="Drop Down Arrow"
          className="side-nav__icon u-flex-center-align"
        >
          <FaChevronRight />
        </button>
      </span>
    )
  } else {
    LinkButton = (
      <button
        aria-expanded={ isOpen }
        aria-owns={ linkAria }
        className={ linkClass }
        onClick={ () => handleSubNav(id) }
        role="treeitem"
        type="button"
      >
        { linkText }
        <span className="side-nav__icon u-flex-center-align">
          <FaChevronRight />
        </span>
      </button>
    )
  }

  return (
    <li className="side-nav__link--wrapper sub-nav" role="none">
      { LinkButton }
      { childPages.length > 0 && (
        <SubNavList
          aria-label={ linkId }
          childPages={ childPages }
          handleCloseNav={ handleCloseNav }
          id={ linkId }
          isOpen={ isOpen }
        />
      ) }
    </li>
  )
}
